import * as React from 'react'
import { ChangeEvent } from 'react';
import { Theme } from '@mui/material/styles'

import { grey } from '@mui/material/colors';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Button,
  Fab,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Divider,
  styled
} from '@mui/material';
import { ChromePicker } from 'react-color';
import Editor from "../../../../core/Editor";
import IEditorSelectionChangeListener from '../../../../core/selection/IEditorSelectionChangeListener';
import EditorSelection from '../../../../core/selection/EditorSelection';
import { Class } from '../../../../../common/core/model/WideoDef';
import Color from "../../../../../api/model/Color";
import WaitingFor from "../WaitingFor";
import Add from '@mui/icons-material/Add';
import Save from '@mui/icons-material/Save'
import Cancel from '@mui/icons-material/Cancel'
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import LensIcon from '@mui/icons-material/Lens';
import NotificationSnackBar from "../NotificationSnackBar";
import update from 'immutability-helper';

import SwitchButton from '../SwitchButton';
import CloseButton from '../../dialogs/CloseButton';

import Logger from '../../../../../common/log/Logger';
import { scrollToElement } from "../../utils/ScrollTo";

import ColorLessItem from './ColorLessItem';

import { WithTranslation, withTranslation } from 'react-i18next';
import { hexToInt, intToHex } from '../../../../../common/ColorUtils';

import { getTransitionColor, getWhiteColor } from '../../dialogs/FontDialog';
import { getBackgroundColor400 } from '../../dialogs/search/SearchProvider';
import { getBackgroundWhiteColor } from '../../dialogs/UploadAssetDialog';
import theme from 'src/common/ui/theme';
import { BrandContext } from 'src/common/ui/BrandContextProvider';

import { _isWideoBrand } from 'src/common/Utils';


export const getMargin = (theme: Theme) => ({
  margin: `${theme.spacing(1)} 0`
});

export const getBorder = (theme: Theme) => ({
  border: `1px solid ${theme.palette.primary[200]}`
});

declare module CP {

  export interface Hsl {
    h: number;
    s: number;
    l: number;
    a: number;
  }

  export interface Rgb {
    r: number;
    g: number;
    b: number;
    a: number;
  }

  export interface Hsv {
    h: number;
    s: number;
    v: number;
    a: number;
  }

  export interface ColorPicked {
    hsl: Hsl;
    hex: string;
    rgb: Rgb;
    hsv: Hsv;
    oldHue: number;
    source: string;
  }
}

const styles = {
  contentColor: {
    transform: 'scale(0.95)',
    transition: 'transform 200ms ease-out',
    textAlign: 'center' as React.CSSProperties['textAlign'],
    margin: '0 auto',
    '&:hover': {
      transform: 'scale(1.15)',
    }
  },
  button: {
    margin: '15px 0',
    width: 'auto',
    height: 'auto',
    boxShadow: 'none',
    color: 'transparent',
    ...getBackgroundWhiteColor(theme),
    padding: '0',
    '&:hover': {
      ...getBackgroundWhiteColor(theme)
    },
  },
  labelIcon: {
    backgroundColor: 'transparent',
    borderRadius: '50%',
    transition: 'box-shadow 150ms ease-in-out, border 150ms ease-in-out',
  },
  sizeSmallIcon: {
    fontSize: '30px',
  },
  sizeSmallIconButton: {
    fontSize: '40px',
    borderRadius: '50%',
  },
  buttonAdd: {
    ...getBorder(theme),
    ...getBackgroundWhiteColor(theme),
    zIndex: 2,
    boxShadow: 'none'
  },
  buttonColorless: {
    boxShadow: 'none',
    background: 'linear-gradient(45deg,#ffffff 49%,#F44336 51%,#ffffff 52%)',
    zIndex: 2,
    width: 'auto',
    height: 'auto',
    padding: '0',
    '&:hover': {
      background: 'linear-gradient(45deg,#d5d5d5 49%,#F44336 51%,#d5d5d5 52%)',
      transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    }
  },
  buttonDeleteColor: {
    boxShadow: 'none',
    right: 0,
    bottom: '8px',
    position: 'absolute' as 'absolute',
    padding: 0,
    '&:hover': {
      background: 'transparent',
    }
  },
  popover: {
    position: 'absolute' as 'absolute',
    zIndex: 2,
    top: '170px',
  },
  cover: {
    position: 'fixed' as 'fixed',
    top: '0',
    right: '0px',
    bottom: '0px',
    left: '0',
  },
  hrLg: {
    margin: '20px 0',
  },
  iconActions: {
    display: 'flex',
    marginLeft: 'auto'
  },
  wrapperCancelDeletableColor: {
    textAlign: 'right' as 'right',
    width: '100%'
  },
  buttonCancelDeletableColor: {
    margin: '15px 0 0'
  },
  nested: {
    marginRight: 0,
  },
  dialog: {
    justifyContent: 'flex-end' as 'flex-end',
    alignItems: 'flex-center' as 'flex-center',
  },
  dialogContent: {
    padding: 0,
    '&:firstOfType': {
      padding: 0
    }
  },
  rootSwitch: {
    width: 42,
    height: 26,
    padding: 0,
    ...getMargin(theme)
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      ...getWhiteColor(theme),
      "& + $track": {
        backgroundColor: "#2287e3",
        opacity: 1,
        border: "none"
      }
    }
  },
  thumb: {
    width: 24,
    height: 24
  },
  track: {
    borderRadius: 13,
    ...getBackgroundColor400(theme),
    opacity: 1,
    ...getTransitionColor(theme),
  },
  checked: {},
  chromePicker: {
    boxShadow: 'none!important'
  }
};

const CustomDialog = styled(Dialog)(() => ({
  '.MuiDialog-scrollPaper': styles.dialog,
}));


interface Props {
  editor: Editor,
  entity?: string,
  parentComponentClass: string,
  handleColorSelected: (color: Color) => void
  handleColorLess: () => void,
  handleGetCurrentObjectColor: () => number
}

interface State {
  colors: Color[],
  colorSelected: Color,
  isColorLessSelected: boolean,
  selectingNewColor: boolean,
  showOptionsToSaveColor: boolean
  snackBarError: boolean,
  snackBarText: string,
  snackBarOpen: boolean,
  deleteActionShow: boolean,
  anchorEl: HTMLElement,
  openDialog: boolean,
  handlerSaveNewColor: () => void, //used to save a new color in user's colors, or used to replace a template color with other
  templateColorToReplace: Color,
  handlerPickNewColor: (colorSelected: CP.ColorPicked) => void,
  applyAll: boolean,
}

export const hexToRGB = (num: number) => {
  // tslint:disable-next-line
  num >>>= 0;
  const b = num & 0xFF;
  const g = (num & 0xFF00) >>> 8;
  const r = (num & 0xFF0000) >>> 16;
  return "rgb(" + [r, g, b].join(",") + ")";
}

const DEFAULT_COLOR_PICKER: Color = new Color(0, 0, 0xffffff);

const defaultColors: Color[] =
  [
    {
      colorId: 0,
      userId: 0,
      rgb: 0x000000
    },
    {
      colorId: 1,
      userId: 0,
      rgb: 0xA8A8A8
    },
    {
      colorId: 2,
      userId: 0,
      rgb: 0xD9D9D9
    },
    {
      colorId: 3,
      userId: 0,
      rgb: 0xFFFFFF
    },
    {
      colorId: 4,
      userId: 0,
      rgb: 0xF44336
    },
    {
      colorId: 5,
      userId: 0,
      rgb: 0xE91E63
    },
    {
      colorId: 6,
      userId: 0,
      rgb: 0x9C27B0
    },
    {
      colorId: 7,
      userId: 0,
      rgb: 0x673AB7
    },
    {
      colorId: 8,
      userId: 0,
      rgb: 0x1eaae4
    },
    {
      colorId: 9,
      userId: 0,
      rgb: 0x4CAF50
    },
    {
      colorId: 10,
      userId: 0,
      rgb: 0x009588
    },
    {
      colorId: 11,
      userId: 0,
      rgb: 0x69e26e
    },
    {
      colorId: 12,
      userId: 0,
      rgb: 0xCDDC39
    },
    {
      colorId: 13,
      userId: 0,
      rgb: 0xFFEB3B
    },
    {
      colorId: 14,
      userId: 0,
      rgb: 0xFFC108
    },
    {
      colorId: 15,
      userId: 0,
      rgb: 0x795548
    }
  ];

const numberOfColorsToShow = 30;


type Styled = any

type PropsWithTranslationsAndStyles = Props & WithTranslation & Styled;

class CommonColorPicker extends React.Component<PropsWithTranslationsAndStyles, State> implements IEditorSelectionChangeListener {

  constructor(props: PropsWithTranslationsAndStyles) {
    super(props);
    this.state = {
      colors: null,
      colorSelected: DEFAULT_COLOR_PICKER,
      isColorLessSelected: false,
      selectingNewColor: false,
      showOptionsToSaveColor: false,
      snackBarOpen: false,
      snackBarText: '',
      snackBarError: false,
      deleteActionShow: false,
      anchorEl: null,
      openDialog: false,
      handlerSaveNewColor: null,
      templateColorToReplace: null,
      handlerPickNewColor: null,
      applyAll: false,

    }
  }

  static contextType = BrandContext;
  context: React.ContextType<typeof BrandContext>

  openSnackbarMessage = (message: string, isError: boolean) => {
    this.setState(() => { return { snackBarOpen: true, snackBarText: message, snackBarError: isError } })
  }

  closeSnackBarMessage = () => {
    this.setState(() => { return { snackBarOpen: false, snackBarText: '', snackBarError: false } })
  }

  handleColorSelected = (color: Color) => {
    this.setState(() => { return { colorSelected: color, isColorLessSelected: false } },
      () => {
        Logger.leaveBreadcrumb('CommonColorPicker::handleColorSelected(), color: ', color);
        this.props.handleColorSelected(color);
      })
  }

  // tslint:disable-next-line
  handleColorless = () => {
    if (this.props.handleColorLess) {
      this.props.handleColorLess();
    }
    this.setState(() => { return { isColorLessSelected: true } })
  }

  //Open color picker
  handleAddNewColor = () => {
    this.setState((prevState: State) => {
      return {
        selectingNewColor: !prevState.selectingNewColor,
        handlerSaveNewColor: this.handleSaveNewColor,
        handlerPickNewColor: this.handlePickNewColor,
        showOptionsToSaveColor: false
      }
    })
  }

  handleAddNewColorToReplace = (templateColorToReplace: Color) => {
    //show selected color on canvas
    this.props.handleColorSelected(templateColorToReplace);

    this.setState((prevState: State) => {
      return {
        selectingNewColor: !prevState.selectingNewColor,
        handlerSaveNewColor: this.handleSaveNewColorToReplace,
        templateColorToReplace: templateColorToReplace,
        colorSelected: templateColorToReplace,
        handlerPickNewColor: this.handlePickNewColorToReplace,
        showOptionsToSaveColor: true
      }
    })
  }

  disableViewportZoom = () => {
    // diseable zoom on iphone
    if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i)) {
      const viewportmeta = document.querySelector('meta[name="viewport"]');
      if (viewportmeta) {
        viewportmeta.setAttribute('content', 'width=device-width, minimum-scale=1.0, maximum-scale=1.0, initial-scale=1.0');
      }
    }

  }

  handleCloseColorPickers = () => {
    this.setState({ selectingNewColor: false })
  }

  handleApplyToObject = () => {
    const color: Color = this.state.colorSelected;
    if (color) {
      this.handleColorSelected(color);
      this.handleCloseColorPickers();
      this.props.editor.logAmplitudeEvent("EDITOR-TEMPLATE-COLOR-APPLY-TO-OBJECT", {});
      this.props.editor.logGAEvent("EDITOR-TEMPLATE-COLOR-APPLY-TO-OBJECT", null, false);
    }

  }

  //template behavior
  //Replace templateColorToReplace with colorSelected. 
  handleSaveNewColorToReplace = () => {
    const color: Color = this.state.colorSelected;
    if (color) {
      this.props.editor.replaceColor(this.state.templateColorToReplace, color);
      this.handleCloseColorPickers();
      this.props.editor.logAmplitudeEvent("EDITOR-TEMPLATE-COLOR-APPLY-TO-ALL", {});
      this.props.editor.logGAEvent("EDITOR-TEMPLATE-COLOR-APPLY-TO-ALL", null, false);

    }
  }

  //default behavior
  //Save new color when user clicks on save color button
  handleSaveNewColor = () => {
    const { t } = this.props;
    const color: Color = this.state.colorSelected;
    if (color) {
      this.props.editor.saveColor(color).then((colorSaved: Color) => {
        const message = t("utils.colors.sections.default-colors.actions.success-message");
        this.openSnackbarMessage(message, false);
        this.handleColorSelected(colorSaved);

        this.setState((prevState: State, prevProps: Props) => {
          let colors = update(prevState.colors, {
            $unshift: [colorSaved]
          });
          colors = colors.slice(0, Math.min(numberOfColorsToShow, colors.length));
          return { selectingNewColor: false, colors: colors }
        });

        this.props.editor.logAmplitudeEvent("CREATE_NEW_COLOR", {});
        this.props.editor.logGAEvent("CREATE_NEW_COLOR", null, false);


      }).catch((error) => {
        const message = t("utils.colors.sections.default-colors.actions.error-message");
        this.openSnackbarMessage(message, true)
      })
    }
  }

  //Get color picked from picker when user clicks on picker. The color user select for replacement
  handlePickNewColorToReplace = (colorSelected: CP.ColorPicked) => {
    const color: Color = new Color(99999, 99999, hexToInt(colorSelected.hex));
    this.handleColorSelected(color);
    this.setState(() => { return { colorSelected: color, isColorLessSelected: false } });
  }

  //Get color picked from picker when user clicks on picker
  handlePickNewColor = (colorSelected: CP.ColorPicked) => {
    const color: Color = new Color(99999, 99999, hexToInt(colorSelected.hex));
    this.handleColorSelected(color);
  }

  handleChangeApplyOption = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ applyAll: event.target.checked });
  }

  showDeletableButton() {
    this.setState(() => {
      return { deleteActionShow: true, anchorEl: null }
    });
    setTimeout(() => scrollToElement('userColors'), 500)
  }

  hideDeletableButton() {
    this.setState(() => {
      return { deleteActionShow: false }
    });
  }

  handleDeleteUserColor(color: Color) {
    const { t } = this.props;
    this.handleCloseDialog();
    this.props.editor.deleteColor(color.colorId).then(() => {

      this.setState((prevState: State, prevProps: Props) => {
        const index: number = prevState.colors.findIndex(colorItem => colorItem.colorId === color.colorId);

        const colors = update(prevState.colors, {
          $splice: [[index, 1]]
        }
        )
        return { colors: colors }
      });
      const message = t("utils.colors.sections.user-colors.actions.delete.actions.success-message");
      this.openSnackbarMessage(message, false)

    })
      .catch((e) => {
        const message = t("utils.colors.sections.user-colors.actions.delete.actions.error-message");
        this.openSnackbarMessage(message, true)
      });
  }

  handleClickColorsActions = event => {
    this.setState({ anchorEl: event.currentTarget });
  }

  handleCloseColorsActions = () => {
    this.setState({ anchorEl: null });
  }

  renderDeletableIconColor(color: Color) {
    if (this.state.deleteActionShow === true) {
      return <IconButton onClick={() => { this.handleClickOpenDialog(color) }}
        style={styles.buttonDeleteColor} aria-label="delete" >
        <Cancel color="error" />
      </IconButton>
    }
    else {
      return <span />
    }
  }

  renderCancelDeletableColor() {
    if (this.state.deleteActionShow === true) {
      const { t } = this.props;
      return <div style={styles.wrapperCancelDeletableColor}>
        <Divider light />
        <Button
          color="primary"
          style={styles.buttonCancelDeletableColor}
          onClick={() => { this.hideDeletableButton() }}
        >
          {t("utils.colors.sections.user-colors.actions.edit.button")}
        </Button>
      </div>
    }
    else {
      return <span />
    }
  }

  renderCustomColorSelector() {
    const { t } = this.props;
    const brandConfig = this.context; 
    const isWideoBrand = _isWideoBrand(brandConfig);

    return (
      <Grid container item key={'customColorSelector'} xs={12} style={styles.popover}>
        {this.state.selectingNewColor &&
          <Grid item>
            <CustomDialog
              onClose={this.handleCloseColorPickers}
              open={this.state.selectingNewColor}
              scroll='paper'
              PaperProps={{
                sx: { backgroundColor: grey[100], borderRadius: isWideoBrand ? 2 : 4 }
              }}
              BackdropProps={{
                invisible: true
              }}
            >
              <DialogContent style={{ ...styles.dialogContent }}>
                <Box px={1} pt={1}>
                  <ChromePicker
                    style={styles.chromePicker}
                    color={intToHex(this.state.colorSelected.rgb)}
                    disableAlpha={true}
                    onChangeComplete={this.state.handlerPickNewColor}
                  />
                </Box>
                {this.state.colorSelected !== DEFAULT_COLOR_PICKER && this.state.selectingNewColor &&
                  <Box p={1} mb={1}>
                    {this.state.showOptionsToSaveColor ?
                      <>
                        {/* <Button
                          cyid="applyToAllButton"
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={this.state.handlerSaveNewColor}
                        >
                          {t("utils.colors.sections.template-colors.actions.apply-all")}
                        </Button>
                        <Button
                          cyid="applyToObjectButton" 
                          size="small"
                          color="primary"
                          onClick={this.handleApplyToObject}
                        >
                          {t("utils.colors.sections.template-colors.actions.apply-object")}
                        </Button> */}
                        <List disablePadding>
                          <ListItem disableGutters>
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  <Box
                                    fontSize={15}
                                    color={this.state.applyAll ? "text.primary" : "text.disabled"}>
                                    {t("utils.colors.sections.template-colors.actions.apply-all")}
                                  </Box>
                                </Typography>}
                            />
                            <ListItemSecondaryAction>
                              <SwitchButton
                                value={this.state.applyAll}
                                checked={this.state.applyAll}
                                variant='xl'
                                size='medium'
                                activeColor="grey.400"
                                handleChange={this.handleChangeApplyOption}
                              />                              
                            </ListItemSecondaryAction>
                          </ListItem>
                        </List>
                        <Button
                          cyid={this.state.applyAll ? 'applyToAllButton' : 'applyToObjectButton'}
                          size="small"
                          sx={{ fontSize: 11 }}
                          color="primary"
                          variant="contained"
                          onClick={this.state.applyAll ? this.state.handlerSaveNewColor : this.handleApplyToObject}
                        >
                          {t("utils.colors.sections.template-colors.actions.apply")}
                        </Button>
                      </> : <Fab
                        style={styles.buttonAdd}
                        size="small"
                        onClick={this.state.handlerSaveNewColor}
                      >
                        <Save color="primary" />
                      </Fab>
                    }
                  </Box>
                }
              </DialogContent>
            </CustomDialog>
          </Grid>}
      </Grid>
    )
  }

  renderColorItems(color: Color, isTemplateColors: boolean) {
    const brandConfig  = this.context;
    const primaryColor = brandConfig?.primaryColor ?? theme.palette.primary.main;
    const isSelected = this.state.colorSelected.rgb === color.rgb
    const whiteColor = color.rgb === 0xffffff
    const colorItemState = isSelected && this.state.isColorLessSelected === false ? primaryColor : `${whiteColor ? grey[300] : grey[100]}`
    const whiteColorActive = isSelected && whiteColor
    const colorBorderState = isSelected ? hexToRGB(0xffffff) : hexToRGB(color.rgb)
    const boxShadowColorState = whiteColorActive ? `${grey[200]} 0px 0px 0px 3px inset, ${hexToRGB(color.rgb)} 0px 0px 0px 25px inset` : `${colorBorderState} 0px 0px 0px 3px inset, ${hexToRGB(color.rgb)} 0px 0px 0px 25px inset`
    const borderColorState = `2px solid ${colorItemState}`
    return (
      <IconButton
        size="small"
        className={[
          "cyObjectColorList",
          isSelected ? "selected" : ""
        ].join(" ")}
        style={{
          ...styles.button,
          label: styles.labelIcon,
          sizeSmall: styles.sizeSmallIconButton
        }}
        onClick={() => {
          Logger.leaveBreadcrumb('CommonColorPicker::renderColorItems(), IconButton callback onClick, color: ', color);
          isTemplateColors ? this.handleAddNewColorToReplace(color) : this.handleColorSelected(color)
        }}
      >
        <LensIcon
          size="small"
          style={{
            ...styles.sizeSmallIconButton,
            boxShadow: boxShadowColorState,
            border: borderColorState
          }}
          viewBox='2 2 20 20'
        />
      </IconButton>)
  }

  renderColorItemsOfObjects = (color: Color): JSX.Element => {
    return this.renderColorItems(color, false)
  }
  renderColorItemsOfTemplates = (color: Color): JSX.Element => {
    return this.renderColorItems(color, true)
  }

  renderListOfColors = (colors: Array<Color>, isDefaultColors: boolean): JSX.Element[] => {
    const result: JSX.Element[] = []
/* 
    const colorLessItem = <Grid item xs={2} key={'colorLessItem'}> 
      <div
        className={styles.contentColor}
      >
        <IconButton
          size="small"
          style={{
            ...this.props.styles.buttonColorless,
            label: this.props.styles.labelIcon,
            sizeSmall: this.props.styles.sizeSmallIconButton

          }}
          onClick={() => { this.handleColorless() }}
        >
          <SvgIcon
            style={{
              border: borderColorLessState,
              color: grey[500]
            }}
            style={{
              ...this.props.styles.sizeSmallIconButton,
            }}
          >
            <path d={ICONS.COLORLESS} />
          </SvgIcon>
        </IconButton>
      </div>
    </Grid>;
    */

    const addNewColorButton = <Grid item key={'addNewColorButton'} xs={2}>
      <div
        style={styles.contentColor}
      >
        <Fab
          style={styles.buttonAdd}
          size="small"
          onClick={this.handleAddNewColor}
        >
          <Add color="primary" />
        </Fab>
      </div>
    </Grid>;

    if (isDefaultColors) {
      result.push(
        <ColorLessItem
          key="colorLessItem"
          handleColorless={this.handleColorless} 
          isColorLessSelected={this.state.isColorLessSelected}
        />
      );
      result.push(addNewColorButton);
      result.push(this.renderCustomColorSelector());
    }

    colors.map((color: Color) => {
      const deleteable: JSX.Element = isDefaultColors ? <span /> : this.renderDeletableIconColor(color);

      result.push(
        <Grid key={color.colorId} item sm={2}>
          <div style={styles.contentColor}>
            {this.renderColorItemsOfObjects(color)}
            {deleteable}
          </div>
        </Grid>)
    });

    return result;
  }

  renderListOfColorsBlock = (colors: Array<Color>, isDefaultColors: boolean): JSX.Element => {
    const colorsJSX: JSX.Element[] = this.renderListOfColors(colors, isDefaultColors)
    return (<Grid item sm={12} container alignItems="center" >{colorsJSX}</Grid>)
  }

  renderListOfUserColors = (): JSX.Element => {
    return this.renderListOfColorsBlock(this.state.colors, false)
  }
  renderListOfDefaultColors = (): JSX.Element => {
    return this.renderListOfColorsBlock(defaultColors, true)
  }

  renderListOfTemplateColors = (): JSX.Element[] => {
    const templateColors = this.props.editor.getAllColorsUsedInWideo();
    const result: JSX.Element[] = []

    templateColors.map((color: Color) => {
      result.push(
        <Grid key={color.colorId} item sm={2}>
          <div style={styles.contentColor}>
            {this.renderColorItemsOfTemplates(color)}
          </div>
        </Grid>
      )
    });

    return result;
  }

  renderTemplateColorsSection = (): JSX.Element => {
    const { t } = this.props;
    const colorsJSX: JSX.Element[] = this.renderListOfTemplateColors()
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="subtitle1" color="inherit" sx={{ color: '#424242', mx: 1 }}>{t("utils.colors.sections.template-colors.title")}</Typography>
        </Grid>
        {colorsJSX}
        <Grid item xs={12}>
          <Divider style={styles.hrLg} />
        </Grid>
      </Grid>
    )
  }

  colorDialog: Color;

  handleClickOpenDialog = (color: Color) => {
    this.setState({ openDialog: true });
    this.colorDialog = color;
  }

  handleCloseDialog = () => {
    this.setState({ openDialog: false });
  }

  renderDialog() {
    const { t } = this.props;

    const brandConfig = this.context; 
    const isWideoBrand = _isWideoBrand(brandConfig);

    return <>
      <Dialog
        maxWidth='sm'
        fullWidth={true}
        open={this.state.openDialog}
        onClose={this.handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: isWideoBrand ? 2 : 4 }
        }}         
      >
        <DialogTitle id="alert-dialog-title">{t("utils.colors.sections.user-colors.actions.delete.title")}</DialogTitle>
        <CloseButton onClose={this.handleCloseDialog} />
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("utils.colors.sections.user-colors.actions.delete.text")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseDialog} color="primary">
            {t("utils.colors.sections.user-colors.actions.delete.actions.cancel")}
          </Button>
          <Button onClick={() => { this.handleDeleteUserColor(this.colorDialog) }} variant="contained" color="primary" autoFocus>
            {t("utils.colors.sections.user-colors.actions.delete.actions.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  }

  loadUserColors = () => {
    this.props.editor.getColors().then((data: Color[]) => {
      // SUPER HACK!!! Only care about the "numberOfColorsToShow" last colors.. this will bite us in the 
      // arse later when users have thousands of colors since the responses will grow to be huge!
      const colors = data.slice(data.length - numberOfColorsToShow, data.length).reverse();
      this.setState(() => { return { colors: colors }; }, () => {
        this.syncSelectedColor();
      })
    }).catch((error) => {
      //TODO: Handle all error cases here...
      Logger.error('Get color failed: %s', Logger.errorToString(error));
      throw error;
    });
  }

  syncSelectedColor = () => {
    const selection = this.props.editor.getSelection();
    if (!this.shouldSyncSelection(selection)) {
      return;
    }
    this.getDefaultColorSelected();
  }

  setStateByDefaultColorSelected(targetColor: number) {
    if (targetColor !== null) {
      let findColor = false
      let findColorInDefaultColors = false
      this.state.colors.map((color: Color) => {
        if (color.rgb === targetColor) {
          findColor = true
          this.setState({ colorSelected: color });
        }
      })
      if (!findColor) {
        defaultColors.map((color: Color) => {
          if (color.rgb === targetColor) {
            findColorInDefaultColors = true
            this.setState({ colorSelected: color });
          }
        })
      }
      if (!findColorInDefaultColors) {
        const templateColors = this.props.editor.getAllColorsUsedInWideo();
        templateColors.map((color: Color) => {
          if (color.rgb === targetColor) {
            this.setState({ colorSelected: color });
          }
        })
      }
    } else { this.setState({ isColorLessSelected: true }) };
  }

  isComponentByClass(classComponent: Class) {
    return this.props.parentComponentClass === classComponent
  }

  private shouldSyncSelection(selection: EditorSelection): boolean {
    switch (this.props.parentComponentClass) {
      case Class.TextComponent:
        return selection.hasTextSelection();
      case Class.ShapeComponent:
        return selection.hasShapeSelection();
      case Class.BackgroundComponent:
        return selection.getSelectedScene() != null;
      default:
        return selection.hasObjectSelected();
    }
  }

  getDefaultColorSelected() {
    if (this.state.colors) {
      const color: number = this.props.handleGetCurrentObjectColor();
      this.setStateByDefaultColorSelected(color);
    }
  }

  componentDidMount() {
    this.props.editor.addSelectionChangeListener(this);
    this.loadUserColors();
    this.disableViewportZoom();
  }

  componentWillUnmount() {
    this.props.editor.removeSelectionChangeListener(this);
  }

  onSelectionChange(selection: EditorSelection): void {
    if (!this.shouldSyncSelection(selection)) {
      return;
    }
    this.syncSelectedColor();
  }

  componentDidUpdate(newProps: Props) {
    //FIXME:Remove entity from here
    if (this.state.colors && newProps.entity !== this.props.entity) {
      this.syncSelectedColor();
    }
  }


  render() {
    const { t } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    return (
      <WaitingFor cyid={"loadingColorPanel"} showLoadingWhileThisIsTrue={this.state.colors === null}>
        <Grid container alignItems="center">
          <this.renderTemplateColorsSection />
          <Grid item xs={12} container>
            <Grid item xs={7}>
              <Typography variant="subtitle1" color="inherit" sx={{ color: '#424242', mx: 1 }}>{t("utils.colors.sections.default-colors.title")}</Typography>
            </Grid>
          </Grid>
          <this.renderListOfDefaultColors />
          <Grid container>
            <Grid item xs={12}>
              <Divider style={styles.hrLg} />
            </Grid>
          </Grid>
          <Grid item xs={12} container id="userColors">
            <Grid item xs={11}>
              <Typography variant="subtitle1" color="inherit" sx={{ color: '#424242', mx: 1 }}>{t("utils.colors.sections.user-colors.title")}</Typography>
            </Grid>
            <Grid item xs={1}>
              <div style={styles.iconActions} >
                <IconButton
                  aria-owns={open ? 'menu-actions-colors' : null}
                  aria-haspopup="true"
                  onClick={this.handleClickColorsActions}
                  color="secondary"
                >
                  <MoreVertIcon
                    color='action'
                  />
                </IconButton>
                <Menu
                  id="menu-actions-colors"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={this.handleCloseColorsActions}
                >
                  <MenuItem
                    onClick={() => { this.showDeletableButton() }}
                  >
                    <ListItemIcon
                      style={{
                        ...styles.nested,
                      }}
                    >
                      <EditIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("utils.colors.sections.user-colors.actions.edit.title")} />
                  </MenuItem>
                </Menu>
                {this.renderDialog()}
              </div>
            </Grid>
            <Grid item xs={12}>
              {this.renderCancelDeletableColor()}
            </Grid>
            <Grid item xs={12}>
              <this.renderListOfUserColors />
            </Grid>
          </Grid>
        </Grid>
        <NotificationSnackBar snackBarOpen={this.state.snackBarOpen} snackBarText={this.state.snackBarText}
          snackBarError={this.state.snackBarError} handleCloseSnackBar={this.closeSnackBarMessage} parentComponent={'CommonColorPicker'} />
      </WaitingFor>)
  }

}

export default withTranslation("utils")(CommonColorPicker);

